import { Fragment } from 'react';

import {
	DefaultErrorComponent,
	DefaultNotFoundComponent,
	type Middleware,
	type RouteComponent,
} from '@shared/router';
import {
	logRouterEntity,
	logRouterError,
	reportRouterError,
} from '@shared/router/logger';
import type { RouteDefinition } from '@shared/router/parser';
import * as uri from '@shared/router/uri';

function invokeMiddlewares(
	middlewares: Middleware[],
	params: RouteDefinition,
	manipulateProps = true
): RouteDefinition {
	if (!middlewares || middlewares.length === 0) return params;

	const assumeLiteralObject = (value: any) =>
		value !== null &&
		typeof value === 'object' &&
		Array.isArray(value) === false;

	Array.from(middlewares).forEach((middleware) => {
		if (!manipulateProps) {
			middleware(params);
		} else {
			try {
				const result = middleware(params);

				if (assumeLiteralObject(result)) {
					params = { ...params, ...result };
				}
			} catch (error) {
				logRouterError(error as Error);

				logRouterError(
					`The above error occured in middleware: ${middleware.name}`
				);

				reportRouterError(error as Error);
			}
		}
	});

	return params;
}

export interface DelegatorProps {
	before?: Middleware[];
	beforeEach?: Middleware[];

	after?: Middleware[];
	afterEach?: Middleware[];

	onError?: (route: RouteDefinition, error: Error) => void;
	onNotFound?: (route: RouteDefinition) => void;

	wrapAllWith?: RouteComponent;
	renderErrorWith?: RouteComponent;
	renderNotFoundWith?: RouteComponent;
}

export function delegator(
	currentPath: string,
	parsedRoutes: RouteDefinition[],
	delegatorProps: DelegatorProps = {}
): RouteDefinition {
	const { onError, onNotFound } = delegatorProps;

	let currentRoute: RouteDefinition = {
		path: '',
		component: () => <Fragment />,
	};

	const notFoundRoute: RouteDefinition = {
		path: currentPath,
		name: 'notFound',
		isValid: false,
		matches: {},
		search: {},
		component: delegatorProps.renderNotFoundWith || DefaultNotFoundComponent,
	};

	const errorRoute: RouteDefinition = {
		...notFoundRoute,
		name: 'error',
		component: delegatorProps.renderErrorWith || DefaultErrorComponent,
	};

	try {
		let parsedUri: uri.RouterPath = uri.EmptyRouterPath;

		currentRoute = parsedRoutes.find((route: RouteDefinition) => {
			parsedUri = uri.parse(route.path, currentPath);

			if (!parsedUri.match) {
				return false;
			}

			if (route.matches && Object.keys(route.matches).length > 0) {
				return Object.entries(route.matches).every(
					([key, matcher]: [string, RegExp]) => {
						return parsedUri.params?.[key]?.toString().match(matcher);
					}
				);
			}

			return true;
		}) as RouteDefinition;

		if (!currentRoute) {
			currentRoute = notFoundRoute;

			if (onNotFound) {
				onNotFound(notFoundRoute);
			}

			logRouterEntity(`Route "${currentPath}" not found.`, 'warn');
		}

		const parentRoute: RouteDefinition = parsedRoutes.find(
			(route: RouteDefinition) => route.path === currentRoute?.parentPath
		) as RouteDefinition;

		if (parentRoute) {
			const parentChildRoutes: RouteDefinition[] = parsedRoutes.filter(
				(route: RouteDefinition) => route?.parentPath === parentRoute.path
			);

			parentRoute.childRoutes = parentChildRoutes;
		}

		const childRoutes: RouteDefinition[] = parsedRoutes.filter(
			(route: RouteDefinition) => route?.parentPath === currentRoute.path
		);

		currentRoute.parentRoute = parentRoute;
		currentRoute.childRoutes = childRoutes;

		currentRoute.params = parsedUri.params;
		currentRoute.search = parsedUri.query;

		if (delegatorProps.wrapAllWith) {
			currentRoute.wrapAllWith = delegatorProps.wrapAllWith;
		}

		if (delegatorProps.beforeEach && delegatorProps.beforeEach.length > 0) {
			currentRoute = invokeMiddlewares(delegatorProps.beforeEach, currentRoute);

			logRouterEntity(
				`Invoked ${delegatorProps.beforeEach.length} beforeEach middlewares.`
			);
		}

		if (currentRoute.before && currentRoute.before.length > 0) {
			currentRoute = invokeMiddlewares(currentRoute.before, currentRoute);

			logRouterEntity(
				`Invoked ${currentRoute.before?.length} before middlewares.`
			);
		}

		// Validate matches conditions
		if (currentRoute.matches && Object.keys(currentRoute.matches).length > 0) {
			currentRoute.isValid = Object.entries(currentRoute.matches).every(
				([key, matcher]: [string, RegExp]) => {
					return currentRoute.params?.[key]?.toString().match(matcher);
				}
			);

			if (currentRoute.isValid === false) {
				logRouterEntity(`Route path does not match conditions`, 'warn');
			}
		}

		// Transform params
		if (
			currentRoute.transforms &&
			Object.keys(currentRoute.transforms).length > 0
		) {
			Object.entries(currentRoute.transforms).forEach(([key, transformer]) => {
				if (currentRoute.params?.[key]) {
					currentRoute.params[key] = transformer(currentRoute.params[key]);
				}
			});
		}

		if (currentRoute.after && currentRoute.after.length > 0) {
			invokeMiddlewares(currentRoute.after, currentRoute);

			logRouterEntity(
				`Invoked ${currentRoute.after.length} after middlewares.`
			);
		}

		if (delegatorProps.afterEach && delegatorProps.afterEach.length > 0) {
			invokeMiddlewares(delegatorProps.afterEach, currentRoute);

			logRouterEntity(
				`Invoked ${delegatorProps.afterEach.length} afterEach middlewares.`
			);
		}
	} catch (error) {
		if (onError && error) {
			currentRoute = errorRoute;

			onError(errorRoute, error as Error);
			logRouterError(error as Error);
		}
	}

	return currentRoute;
}
